import { ICH_ORG_IDS, isIchOrganization } from "../../constants/ichOrganizations";
export const HCA_ORG_ID = "75a99644-d769-11ec-8330-069463faa5ff";
export const ENP_HG_ORG_ID = "cf812b0c-5695-11f1-b68d-0622d9facbbd";
export const JAB_CC_ORG_ID = "332a9d47-6e36-11e8-a392-061714f61e3e";
export const ICH_ORG_ID = "a25f9e42-23cd-11ec-9068-066917b38554";
export const MILLIES_ORG_ID = "282c9985-14ed-11e9-a392-061714f61e3e";
export const DASHBOARD_PREFERENCE_NAME = "dashboard-settings";

export const DASHBOARD_KEYS = Object.freeze({
	COOKIE_TRACEABILITY: "cookie-traceability",
	CC_JOBS: "cc-jobs",
	ICH_HOMES: "ich-homes",
	MILLIES_OPERATIONS: "millies-operations",
	AMBIFI: "ambifi",
	LEGACY: "legacy"
});

const DASHBOARD_DEFINITIONS = Object.freeze([
	Object.freeze({
		key: DASHBOARD_KEYS.COOKIE_TRACEABILITY,
		label: "Cookie Production & Traceability",
		orgIds: Object.freeze([ENP_HG_ORG_ID]),
		aliases: Object.freeze([])
	}),
	Object.freeze({
		key: DASHBOARD_KEYS.CC_JOBS,
		label: "Converge Custom Jobs",
		orgIds: Object.freeze([JAB_CC_ORG_ID]),
		aliases: Object.freeze(["converge-custom", "cc", "jobs"])
	}),
	Object.freeze({
		key: DASHBOARD_KEYS.ICH_HOMES,
		label: "ICH Homes",
		orgIds: ICH_ORG_IDS,
		aliases: Object.freeze(["ich", "homes", "ich-homes-dashboard"])
	}),
	Object.freeze({
		key: DASHBOARD_KEYS.MILLIES_OPERATIONS,
		label: "Millie's Factory Operations",
		orgIds: Object.freeze([MILLIES_ORG_ID]),
		aliases: Object.freeze(["millies", "factory-operations"])
	}),
	Object.freeze({
		key: DASHBOARD_KEYS.AMBIFI,
		label: "AmbiFi Usage Dashboard",
		aliases: Object.freeze([])
	}),
	Object.freeze({
		key: DASHBOARD_KEYS.LEGACY,
		label: "Legacy Dashboard",
		aliases: Object.freeze(["classic", "old"])
	})
]);

function normalizeDashboardKey(value) {
	return typeof value === "string" ? value.trim().toLowerCase() : "";
}

function safeDecodeQueryPart(value) {
	try {
		return decodeURIComponent(`${value || ""}`.replace(/\+/g, " "));
	} catch (error) {
		return "";
	}
}

function parseDashboardSettings(value) {
	if (typeof value === "string") {
		try {
			value = JSON.parse(value);
		} catch (error) {
			return {};
		}
	}
	return value && typeof value === "object" && !Array.isArray(value) ? value : {};
}

export function getSelectedOrgId(selectedOrg = {}) {
	return selectedOrg && typeof selectedOrg.id === "string" ? selectedOrg.id.trim() : "";
}

export function getDashboardQueryValue(location = {}) {
	const search = location && typeof location.search === "string" ? location.search.replace(/^\?/, "") : "";
	if (!search) {
		return "";
	}

	const pairs = search.split("&");
	for (let i = 0; i < pairs.length; i += 1) {
		const separatorIndex = pairs[i].indexOf("=");
		const rawKey = separatorIndex === -1 ? pairs[i] : pairs[i].substring(0, separatorIndex);
		const rawValue = separatorIndex === -1 ? "" : pairs[i].substring(separatorIndex + 1);
		const queryKey = safeDecodeQueryPart(rawKey);
		if (queryKey === "dashboard" || queryKey === "customDashboard") {
			return safeDecodeQueryPart(rawValue);
		}
	}
	return "";
}

export function getDashboardSettings(selectedOrg = {}) {
	const preferences = selectedOrg && selectedOrg.preferences && typeof selectedOrg.preferences === "object"
		? selectedOrg.preferences
		: {};
	const preferencesDefinition = selectedOrg && selectedOrg.preferencesDefinition && typeof selectedOrg.preferencesDefinition === "object"
		? selectedOrg.preferencesDefinition
		: {};
	const value = Object.prototype.hasOwnProperty.call(preferences, DASHBOARD_PREFERENCE_NAME)
		? preferences[DASHBOARD_PREFERENCE_NAME]
		: preferencesDefinition[DASHBOARD_PREFERENCE_NAME];
	return parseDashboardSettings(value);
}

export function isDashboardAvailable(definition, selectedOrg = {}) {
	if (!definition) {
		return false;
	}
	if (!Array.isArray(definition.orgIds) || definition.orgIds.length === 0) {
		return true;
	}
	return definition.orgIds.includes(getSelectedOrgId(selectedOrg));
}

export function getDashboardDefinition(value, selectedOrg = {}) {
	const dashboardKey = normalizeDashboardKey(value);
	if (!dashboardKey) {
		return null;
	}
	const definition = DASHBOARD_DEFINITIONS.find(candidate => (
		candidate.key === dashboardKey ||
		(Array.isArray(candidate.aliases) && candidate.aliases.includes(dashboardKey))
	));
	return isDashboardAvailable(definition, selectedOrg) ? definition : null;
}

export function getDashboardLabel(definition, selectedOrg = {}) {
	if (!definition) {
		return "";
	}
	if (definition.key === DASHBOARD_KEYS.LEGACY && getSelectedOrgId(selectedOrg) === HCA_ORG_ID) {
		return "Audit Dashboard";
	}
	return definition.label;
}

function getOrgAvailableDashboardDefinition(value, selectedOrg = {}) {
	return getDashboardDefinition(value, selectedOrg);
}

function getConfiguredDashboardKeys(selectedOrg = {}) {
	const dashboardSettings = getDashboardSettings(selectedOrg);
	if (!Array.isArray(dashboardSettings.enabledDashboards) || dashboardSettings.enabledDashboards.length === 0) {
		return null;
	}

	const configuredKeys = dashboardSettings.enabledDashboards.reduce((keys, value) => {
		const definition = getOrgAvailableDashboardDefinition(value, selectedOrg);
		if (definition && !keys.includes(definition.key)) {
			keys.push(definition.key);
		}
		return keys;
	}, []);
	return configuredKeys.length > 0 ? configuredKeys : null;
}

function getAllowedDashboardKeys(selectedOrg = {}, retainedDashboardKey = "") {
	const configuredKeys = getConfiguredDashboardKeys(selectedOrg);
	if (configuredKeys === null) {
		return null;
	}

	const allowedKeys = configuredKeys.slice();
	const dashboardSettings = getDashboardSettings(selectedOrg);
	const preferredDefinition = getOrgAvailableDashboardDefinition(dashboardSettings.defaultDashboard, selectedOrg);
	const retainedDefinition = getOrgAvailableDashboardDefinition(retainedDashboardKey, selectedOrg);
	[preferredDefinition, retainedDefinition].forEach(definition => {
		if (definition && !allowedKeys.includes(definition.key)) {
			allowedKeys.push(definition.key);
		}
	});
	return allowedKeys;
}

export function getAvailableDashboards(selectedOrg = {}, retainedDashboardKey = "") {
	const allowedKeys = getAllowedDashboardKeys(selectedOrg, retainedDashboardKey);
	return DASHBOARD_DEFINITIONS
		.filter(definition => (
			isDashboardAvailable(definition, selectedOrg) &&
			(definition.key !== DASHBOARD_KEYS.LEGACY || getSelectedOrgId(selectedOrg) === HCA_ORG_ID) &&
			(allowedKeys === null || allowedKeys.includes(definition.key))
		))
		.map(definition => ({
			key: definition.key,
			label: getDashboardLabel(definition, selectedOrg)
		}));
}

export function resolveDashboardKey(location = {}, selectedOrg = {}) {
	const dashboardSettings = getDashboardSettings(selectedOrg);
	const preferredDefinition = getOrgAvailableDashboardDefinition(dashboardSettings.defaultDashboard, selectedOrg);
	const allowedKeys = getAllowedDashboardKeys(selectedOrg, preferredDefinition ? preferredDefinition.key : "");
	const explicitDefinition = getOrgAvailableDashboardDefinition(getDashboardQueryValue(location), selectedOrg);
	if (explicitDefinition && (allowedKeys === null || allowedKeys.includes(explicitDefinition.key))) {
		return explicitDefinition.key;
	}

	if (preferredDefinition) {
		return preferredDefinition.key;
	}

	if (getSelectedOrgId(selectedOrg) === JAB_CC_ORG_ID && (allowedKeys === null || allowedKeys.includes(DASHBOARD_KEYS.CC_JOBS))) {
		return DASHBOARD_KEYS.CC_JOBS;
	}

	if (getSelectedOrgId(selectedOrg) === MILLIES_ORG_ID && (allowedKeys === null || allowedKeys.includes(DASHBOARD_KEYS.MILLIES_OPERATIONS))) {
		return DASHBOARD_KEYS.MILLIES_OPERATIONS;
	}

	if (isIchOrganization(getSelectedOrgId(selectedOrg)) && (allowedKeys === null || allowedKeys.includes(DASHBOARD_KEYS.ICH_HOMES))) {
		return DASHBOARD_KEYS.ICH_HOMES;
	}

	return DASHBOARD_KEYS.AMBIFI;
}

export function getDashboardPath(value) {
	const dashboardKey = normalizeDashboardKey(value);
	const definition = DASHBOARD_DEFINITIONS.find(candidate => (
		candidate.key === dashboardKey ||
		(Array.isArray(candidate.aliases) && candidate.aliases.includes(dashboardKey))
	));
	const resolvedKey = definition ? definition.key : DASHBOARD_KEYS.AMBIFI;
	return `/dashboard?dashboard=${encodeURIComponent(resolvedKey)}`;
}
